import React, {
    useEffect, useLayoutEffect, useRef, useState,
} from 'react';
import Button from 'antd/lib/button';
import Popover from 'antd/lib/popover';
import Spin from 'antd/lib/spin';
import { QuestionCircleOutlined, RotateRightOutlined } from '@ant-design/icons';

import { t } from 'utils/i18n';
import {
    cssTransform, Placement, Point, Size, wrapAngle,
} from './align-transform';

// the view zooms this much a pixel the wheel scrolls: about 1.2 times a notch of a mouse, smooth on a touchpad
const WHEEL_ZOOM = 0.002;
// + and - zoom as much as a notch of a mouse wheel
const KEY_ZOOM = Math.exp(100 * WHEEL_ZOOM);
const MAX_ZOOM = 16;
// adjusting by hand: the room left around the drawing for the handles of its frame, and how far the view
// zooms out to show a drawing that has grown beyond the first one
export const ADJUST_MARGIN = 40;
const MIN_ADJUST_ZOOM = 0.25;
const NUDGE = 1;
const SHIFT_NUDGE = 10;
// Ctrl+wheel scales the drawing this much a pixel scrolled (2% a notch), with Shift a tenth of it
const WHEEL_SCALE = 0.0002;
const SHIFT_WHEEL_SCALE = 0.00002;
// a wheel that scrolls by lines or pages, in pixels
const DELTA_UNITS = [1, 16, 400];
// Shift snaps a turn to this many degrees, and a scale to whole percents
const TURN_SNAP = 5;
// Shift+drag on the drawing: how far the cursor must leave the point pressed before its direction sets the turn
const TURN_START = 8;
const MIN_SCALE = 0.05;
// keys and Ctrl+wheel pressed within this many ms change the drawing in one step (one undo)
const BURST = 300;
const CORNERS = ['tl', 'tr', 'bl', 'br'] as const;
type Corner = typeof CORNERS[number];
// a press that moves less than this is a click (a point is put), more moves the view
const CLICK = 4;

// adjusting by hand: the drawing as it is, put where ``placement`` says
export interface Adjust {
    placement: Placement | null;
    // the drawing is out of view for a moment, to see the lines under it
    hidden?: boolean;
    // the drawing has loaded: its natural size
    onLoad(drawing: Size): void;
    // a gesture or a burst of keys is about to change the placement: what it is now can be undone to
    onBegin?(): void;
    onChange(placement: Placement): void;
    // putting points: only one of the drawings is shown and a click puts a point on it
    picking?: Picking;
}

export interface Picking {
    show: 'base' | 'over';
    // the points put so far, in pixels of the first drawing, numbered from 1
    marks: (Point & { label: string; on: 'base' | 'over' })[];
    // in pixels of the first drawing
    onPick(point: Point): void;
}

interface Props {
    // each image as a list of addresses: the next one is tried when one cannot be shown
    base: string[];
    over: string[];
    // the full sizes of the images: what is loaded is a lighter copy, the pixels are those of the full image
    baseSize: Size;
    overSize: Size;
    baseName: string;
    overName: string;
    adjust?: Adjust;
}

type Gesture =
    | { kind: 'pan'; x: number; y: number; left: number; top: number }
    // a click puts a point, unless it moves on and becomes a pan
    | { kind: 'pick'; x: number; y: number; left: number; top: number }
    | { kind: 'move'; x: number; y: number; start: Placement }
    // the round handle: around the centre; offset is the angle of the drawing less that of the cursor
    | { kind: 'rotate'; offset: number; start: Placement }
    // Shift+drag on the drawing: around the point pressed (pixels of the first drawing); the angle is set once
    // the cursor moves off it
    | { kind: 'turn'; pivot: Point; angle: number | null; start: Placement }
    // a corner handle: the opposite corner (pixels of the first drawing) stays where it is
    | { kind: 'scale'; anchor: Point; hx: 1 | -1; vy: 1 | -1; start: Placement };

// what the mouse and the keys do: the keys of a row are pressed together
type Control = [keys: string[], action: string];

const VIEW_CONTROLS: Control[] = [
    [['Wheel'], 'Zoom in or out'],
    [['+ / −'], 'Zoom in or out'],
    [['Drag'], 'Move the view'],
    [['Double click'], 'Fit the drawing'],
    [['↑', '↓'], 'Previous or next drawing'],
    [['Space'], 'Before or after'],
];

const ADJUST_CONTROLS: Control[] = [
    [['Drag'], 'Move the drawing'],
    [['Space'], 'Hide or show the drawing'],
    [['Esc'], 'Stop adjusting'],
    [['Click'], 'Put a point, when aligning by two points'],
    [['Corner handle'], 'Scale the drawing, with Shift by whole percents'],
    [['Round handle'], 'Turn the drawing, with Shift by 5°'],
    [['Shift', 'Drag'], 'Turn around the point pressed'],
    [['Ctrl', 'Wheel'], 'Scale the drawing, with Shift finer'],
    [['Arrows'], 'Nudge by a pixel, with Shift by ten'],
    [['Ctrl', 'Z'], 'Undo'],
    [['Ctrl', 'Shift', 'Z'], 'Redo'],
    [['Wheel'], 'Zoom in or out'],
    [['+ / −'], 'Zoom in or out'],
    [['Ctrl', 'Drag'], 'Move the view'],
    [['Double click'], 'Fit the drawing'],
];

function Controls({ controls }: { controls: Control[] }): JSX.Element {
    return (
        <dl className='cvat-align-review-controls'>
            {controls.map(([keys, action]) => (
                <React.Fragment key={action + keys.join()}>
                    <dt>
                        {keys.map((key, index) => (
                            <React.Fragment key={key}>
                                {index > 0 && ' + '}
                                <kbd>{t(key)}</kbd>
                            </React.Fragment>
                        ))}
                    </dt>
                    <dd>{t(action)}</dd>
                </React.Fragment>
            ))}
        </dl>
    );
}

const sizeOf = (image: HTMLImageElement): Size => ({ width: image.naturalWidth, height: image.naturalHeight });

interface PictureProps {
    sources: string[];
    alt: string;
    style?: React.CSSProperties;
    onLoad(size: Size): void;
    onFail(): void;
}

function Picture({
    sources, alt, style, onLoad, onFail,
}: PictureProps): JSX.Element {
    const [index, setIndex] = useState(0);
    return (
        <img
            src={sources[index]}
            alt={alt}
            style={style}
            draggable={false}
            onLoad={(event) => onLoad(sizeOf(event.currentTarget))}
            onError={() => (index + 1 < sources.length ? setIndex(index + 1) : onFail())}
        />
    );
}

// The drawing in red over the one it is aligned to in blue: where the lines meet they turn dark.
// The wheel zooms at the cursor, dragging moves the view (or the drawing, when adjusting), a double
// click fits.
export default function AlignOverlay({
    base, over, baseSize, overSize, baseName, overName, adjust,
}: Props): JSX.Element {
    const viewport = useRef<HTMLDivElement>(null);
    const stack = useRef<HTMLDivElement>(null);
    const anchor = useRef<{ fx: number; fy: number; x: number; y: number } | null>(null);
    const gesture = useRef<Gesture | null>(null);
    const [zoom, setZoom] = useState(1);
    const zoomRef = useRef(1);
    // when the last key or Ctrl+wheel changed the drawing: a pause starts another step
    const burst = useRef(0);
    const [box, setBox] = useState<Size>({ width: 0, height: 0 });
    // the images loaded and failed, by their first address: a new pair waits for its own
    const [shown, setShown] = useState<Record<string, boolean>>({});
    const [failed, setFailed] = useState<Record<string, boolean>>({});
    // what the listeners below need of the latest render
    const latest = useRef<{ adjust?: Adjust; placement: Placement | null; scale: number }>({
        placement: null, scale: 1,
    });

    const [baseKey, overKey] = [base[0], over[0]];
    const ready = !!shown[baseKey] && !!shown[overKey];
    const canvas = ready ? baseSize : null;
    const drawing = ready ? overSize : null;
    const broken = failed[baseKey] || failed[overKey];
    const loaded = (key: string) => (): void => setShown((prev) => ({ ...prev, [key]: true }));
    const fail = (key: string) => (): void => setFailed((prev) => ({ ...prev, [key]: true }));

    useEffect(() => {
        const element = viewport.current as HTMLDivElement;
        const measure = (): void => setBox({ width: element.clientWidth, height: element.clientHeight });
        measure();
        if (typeof ResizeObserver === 'undefined') return undefined;
        const observer = new ResizeObserver(measure);
        observer.observe(element);
        return () => observer.disconnect();
    }, []);

    const beginStep = (current: Adjust): void => {
        const now = Date.now();
        if (now - burst.current > BURST) current.onBegin?.();
        burst.current = now;
    };

    // zooms keeping the point of the view at (x, y) of the screen where it is
    const zoomTo = (factor: number, x: number, y: number): void => {
        const rect = (stack.current as HTMLDivElement).getBoundingClientRect();
        anchor.current = {
            fx: (x - rect.left) / (rect.width || 1), fy: (y - rect.top) / (rect.height || 1), x, y,
        };
        const next = zoomRef.current * factor;
        zoomRef.current = Math.min(MAX_ZOOM, Math.max(latest.current.adjust ? MIN_ADJUST_ZOOM : 1, next));
        setZoom(zoomRef.current);
    };
    const zoomToRef = useRef(zoomTo);
    zoomToRef.current = zoomTo;

    // a non-passive listener: React's onWheel could not keep the page from scrolling
    useEffect(() => {
        const element = viewport.current as HTMLDivElement;
        const onWheel = (event: WheelEvent): void => {
            event.preventDefault();
            const rect = (stack.current as HTMLDivElement).getBoundingClientRect();
            // with Shift some browsers turn the wheel sideways
            const delta = (event.deltaY || event.deltaX) * (DELTA_UNITS[event.deltaMode] ?? 1);
            const { adjust: adjusted, placement: at, scale: zoomed } = latest.current;
            // not Alt: releasing it opens the menu bar of some browsers
            if ((event.ctrlKey || event.metaKey) && adjusted && at) {
                beginStep(adjusted);
                // scales the drawing around the point under the cursor, which stays where it is
                const factor = Math.exp(-delta * (event.shiftKey ? SHIFT_WHEEL_SCALE : WHEEL_SCALE));
                const x = (event.clientX - rect.left) / zoomed;
                const y = (event.clientY - rect.top) / zoomed;
                adjusted.onChange({
                    ...at,
                    cx: x + (at.cx - x) * factor,
                    cy: y + (at.cy - y) * factor,
                    scale: at.scale * factor,
                });
                return;
            }
            zoomToRef.current(Math.exp(-delta * WHEEL_ZOOM), event.clientX, event.clientY);
        };
        element.addEventListener('wheel', onWheel, { passive: false });
        return () => element.removeEventListener('wheel', onWheel);
    }, []);

    // + and - zoom around the middle of the view
    useEffect(() => {
        const onKeyDown = (event: KeyboardEvent): void => {
            const target = event.target as HTMLElement;
            const factors: Record<string, number> = {
                '+': KEY_ZOOM, '=': KEY_ZOOM, '-': 1 / KEY_ZOOM, _: 1 / KEY_ZOOM,
            };
            const factor = factors[event.key];
            if (!factor || event.ctrlKey || event.metaKey || event.altKey ||
                ['INPUT', 'TEXTAREA'].includes(target.tagName)) return;
            event.preventDefault();
            const rect = (viewport.current as HTMLDivElement).getBoundingClientRect();
            zoomToRef.current(factor, rect.left + rect.width / 2, rect.top + rect.height / 2);
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, []);

    // keeps the point under the cursor where it was
    useLayoutEffect(() => {
        const point = anchor.current;
        if (!point) return;
        anchor.current = null;
        const element = viewport.current as HTMLDivElement;
        const rect = (stack.current as HTMLDivElement).getBoundingClientRect();
        element.scrollLeft += rect.left + point.fx * rect.width - point.x;
        element.scrollTop += rect.top + point.fy * rect.height - point.y;
    }, [zoom]);

    const placement = adjust?.placement ?? null;
    useEffect(() => {
        if (adjust && !adjust.placement && canvas && drawing) adjust.onLoad(drawing);
    }, [adjust, canvas, drawing]);

    const aspect = canvas ? canvas.width / canvas.height : 1.4;
    const margin = adjust ? 2 * ADJUST_MARGIN : 0;
    const fit = Math.max(0, Math.min(box.width - margin, (box.height - margin) * aspect));
    const width = fit * zoom;
    // screen pixels per pixel of the first drawing
    const scale = canvas && width ? width / canvas.width : 1;
    latest.current = { adjust, placement, scale };

    // the arrows nudge the drawing by a screen pixel, with Shift by ten
    const adjusting = !!adjust;
    useEffect(() => {
        if (!adjusting) return undefined;
        const steps: Record<string, [number, number]> = {
            ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1],
        };
        const onKeyDown = (event: KeyboardEvent): void => {
            const { adjust: current, placement: at, scale: zoomed } = latest.current;
            const step = steps[event.key];
            const target = event.target as HTMLElement;
            if (!step || !current || !at || current.picking || ['INPUT', 'TEXTAREA'].includes(target.tagName)) return;
            event.preventDefault();
            beginStep(current);
            const distance = (event.shiftKey ? SHIFT_NUDGE : NUDGE) / zoomed;
            current.onChange({ ...at, cx: at.cx + step[0] * distance, cy: at.cy + step[1] * distance });
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [adjusting]);

    const toCanvas = (x: number, y: number): { x: number; y: number } => {
        const rect = (stack.current as HTMLDivElement).getBoundingClientRect();
        return { x: (x - rect.left) / scale, y: (y - rect.top) / scale };
    };

    // the angle of the cursor around the centre of the drawing, in degrees
    const angleAt = (at: Placement, point: Point): number => (
        Math.atan2(point.y - at.cy, point.x - at.cx) * (180 / Math.PI)
    );
    // the active handle, for the angle or the scale shown beside it
    const [handling, setHandling] = useState<'rotate' | 'scale' | null>(null);

    const grabHandle = (handle: string, at: Placement, size: Size, point: Point): Gesture => {
        if (handle === 'rotate') return { kind: 'rotate', offset: at.angle - angleAt(at, point), start: at };
        const hx = handle === 'tl' || handle === 'bl' ? -1 : 1;
        const vy = handle === 'tl' || handle === 'tr' ? -1 : 1;
        const radians = at.angle * (Math.PI / 180);
        const [x, y] = [(-hx * size.width * at.scale) / 2, (-vy * size.height * at.scale) / 2];
        return {
            kind: 'scale',
            anchor: {
                x: at.cx + x * Math.cos(radians) - y * Math.sin(radians),
                y: at.cy + x * Math.sin(radians) + y * Math.cos(radians),
            },
            hx,
            vy,
            start: at,
        };
    };

    const onMouseDown = (event: React.MouseEvent<HTMLDivElement>): void => {
        event.preventDefault();
        // the canvas takes the keys: the arrows and Space must not go on to a field or a button
        (document.activeElement as HTMLElement | null)?.blur();
        const { clientX: x, clientY: y } = event;
        const element = event.currentTarget;
        if (adjust?.picking && event.button === 0) {
            gesture.current = {
                kind: 'pick', x, y, left: element.scrollLeft, top: element.scrollTop,
            };
            return;
        }
        const handle = (event.target as HTMLElement).closest?.('[data-align-handle]')?.getAttribute('data-align-handle');
        if (adjust && placement && drawing && handle && event.button === 0) {
            adjust.onBegin?.();
            gesture.current = grabHandle(handle, placement, drawing, toCanvas(x, y));
            setHandling(handle === 'rotate' ? 'rotate' : 'scale');
            return;
        }
        if (adjust && placement && event.button === 0 && !event.ctrlKey) {
            adjust.onBegin?.();
            gesture.current = event.shiftKey ? {
                kind: 'turn', pivot: toCanvas(x, y), angle: null, start: placement,
            } : {
                kind: 'move', x, y, start: placement,
            };
            return;
        }
        gesture.current = {
            kind: 'pan', x, y, left: element.scrollLeft, top: element.scrollTop,
        };
    };

    const onMouseUp = (event: React.MouseEvent<HTMLDivElement>): void => {
        const { current } = gesture;
        gesture.current = null;
        setHandling(null);
        if (current?.kind === 'pick') adjust?.picking?.onPick(toCanvas(event.clientX, event.clientY));
    };

    const onMouseMove = (event: React.MouseEvent<HTMLDivElement>): void => {
        let { current } = gesture;
        if (!current) return;
        const { clientX: x, clientY: y } = event;
        if (current.kind === 'pick') {
            if (Math.hypot(x - current.x, y - current.y) < CLICK) return;
            current = { ...current, kind: 'pan' };
            gesture.current = current;
        }
        if (current.kind === 'pan') {
            event.currentTarget.scrollLeft = current.left - (x - current.x);
            event.currentTarget.scrollTop = current.top - (y - current.y);
        } else if (current.kind === 'move') {
            adjust?.onChange({
                ...current.start,
                cx: current.start.cx + (x - current.x) / scale,
                cy: current.start.cy + (y - current.y) / scale,
            });
        } else if (current.kind === 'turn') {
            const { pivot, start } = current;
            const point = toCanvas(x, y);
            if (Math.hypot(point.x - pivot.x, point.y - pivot.y) * scale < TURN_START) return;
            const angle = Math.atan2(point.y - pivot.y, point.x - pivot.x);
            if (current.angle === null) {
                current.angle = angle;
                return;
            }
            const turn = angle - current.angle;
            const [cos, sin] = [Math.cos(turn), Math.sin(turn)];
            const [dx, dy] = [start.cx - pivot.x, start.cy - pivot.y];
            adjust?.onChange({
                ...start,
                cx: pivot.x + dx * cos - dy * sin,
                cy: pivot.y + dx * sin + dy * cos,
                angle: wrapAngle(start.angle + turn * (180 / Math.PI)),
            });
        } else if (current.kind === 'rotate') {
            const angle = angleAt(current.start, toCanvas(x, y)) + current.offset;
            const snapped = event.shiftKey ? Math.round(angle / TURN_SNAP) * TURN_SNAP : angle;
            adjust?.onChange({ ...current.start, angle: wrapAngle(snapped) });
        } else if (drawing) {
            // the cursor in the frame of the drawing, from the corner that stays
            const {
                anchor: fixed, hx, vy, start,
            } = current;
            const radians = start.angle * (Math.PI / 180);
            const [cos, sin] = [Math.cos(radians), Math.sin(radians)];
            const point = toCanvas(x, y);
            const [dx, dy] = [point.x - fixed.x, point.y - fixed.y];
            const [localX, localY] = [dx * cos + dy * sin, -dx * sin + dy * cos];
            const grown = Math.max((localX * hx) / drawing.width, (localY * vy) / drawing.height);
            let next = Math.max(MIN_SCALE, grown);
            if (event.shiftKey) next = Math.max(MIN_SCALE, Math.round(next * 100) / 100);
            const [ox, oy] = [(hx * drawing.width * next) / 2, (vy * drawing.height * next) / 2];
            adjust?.onChange({
                ...start,
                scale: next,
                cx: fixed.x + ox * cos - oy * sin,
                cy: fixed.y + ox * sin + oy * cos,
            });
        }
    };

    return (
        <>
            <div
                ref={viewport}
                role='presentation'
                className={`cvat-align-review-viewport${adjust ? ' cvat-align-review-viewport-adjust' : ''}${
                    adjust?.picking ? ' cvat-align-review-viewport-picking' : ''}`}
                onMouseDown={onMouseDown}
                onMouseMove={onMouseMove}
                onMouseUp={onMouseUp}
                onMouseLeave={() => {
                    gesture.current = null;
                    setHandling(null);
                }}
                onDoubleClick={() => {
                    zoomRef.current = 1;
                    setZoom(1);
                }}
            >
                <div
                    ref={stack}
                    className='cvat-align-review-stack'
                    style={{ width: width || `${zoom * 100}%`, visibility: ready ? 'visible' : 'hidden' }}
                >
                    <div
                        className='cvat-align-review-layer cvat-align-review-layer-base'
                        style={adjust?.picking?.show === 'over' ? { visibility: 'hidden' } : undefined}
                    >
                        <Picture
                            key={baseKey}
                            sources={base}
                            alt={baseName}
                            onLoad={loaded(baseKey)}
                            onFail={fail(baseKey)}
                        />
                    </div>
                    {adjust ? (
                        <div className='cvat-align-review-layer cvat-align-review-layer-over cvat-align-review-layer-free'>
                            <div
                                className='cvat-align-review-moving'
                                style={drawing ? {
                                    width: drawing.width,
                                    height: drawing.height,
                                    transform: placement ? cssTransform(placement, drawing, scale) : undefined,
                                    visibility: placement && !adjust.hidden && adjust.picking?.show !== 'base' ?
                                        undefined : 'hidden',
                                } : undefined}
                            >
                                <Picture
                                    key={overKey}
                                    sources={over}
                                    alt={overName}
                                    onLoad={loaded(overKey)}
                                    onFail={fail(overKey)}
                                />
                            </div>
                        </div>
                    ) : (
                        <div className='cvat-align-review-layer cvat-align-review-layer-over'>
                            {/* at its own size on the first drawing: the drawing as it is may be of another size */}
                            <Picture
                                key={overKey}
                                sources={over}
                                alt={overName}
                                style={drawing && canvas ?
                                    { width: `${(drawing.width / canvas.width) * 100}%` } : undefined}
                                onLoad={loaded(overKey)}
                                onFail={fail(overKey)}
                            />
                        </div>
                    )}
                    {adjust && placement && drawing && !adjust.picking && !adjust.hidden && (
                        // the frame of the drawing, turned with it; its handles keep their size on the screen
                        <div
                            className='cvat-align-review-frame'
                            style={{
                                left: placement.cx * scale,
                                top: placement.cy * scale,
                                width: drawing.width * placement.scale * scale,
                                height: drawing.height * placement.scale * scale,
                                transform: `translate(-50%, -50%) rotate(${placement.angle}deg)`,
                            }}
                        >
                            {CORNERS.map((corner: Corner) => (
                                <span
                                    key={corner}
                                    data-align-handle={corner}
                                    className={`cvat-align-review-handle cvat-align-review-handle-${corner}`}
                                />
                            ))}
                            <span
                                data-align-handle='rotate'
                                className='cvat-align-review-rotate'
                                title={t('Drag to turn; with Shift by 5°')}
                                aria-label={t('Turn the drawing')}
                            >
                                <RotateRightOutlined />
                            </span>
                            {handling && (
                                <span
                                    className='cvat-align-review-badge'
                                    style={{ transform: `translateX(-50%) rotate(${-placement.angle}deg)` }}
                                >
                                    {handling === 'rotate' ?
                                        `${Math.round(placement.angle * 10) / 10}°` :
                                        `${Math.round(placement.scale * 1000) / 10}%`}
                                </span>
                            )}
                        </div>
                    )}
                    {adjust?.picking?.marks.filter((mark) => mark.on === adjust.picking?.show).map((mark) => (
                        <span
                            key={mark.label}
                            className={`cvat-align-review-mark cvat-align-review-mark-${mark.on}`}
                            style={{ left: mark.x * scale, top: mark.y * scale }}
                        >
                            {mark.label}
                        </span>
                    ))}
                </div>
                {!ready && (
                    <div className='cvat-align-review-loading'>
                        {broken ? t('The drawing cannot be shown') : <Spin />}
                    </div>
                )}
            </div>
            <div className='cvat-align-review-toolbar'>
                <span className='cvat-align-review-legend cvat-align-review-legend-over'>{overName}</span>
                <span className='cvat-align-review-legend cvat-align-review-legend-base'>{baseName}</span>
                <span className='cvat-align-review-hint'>{t('Where the colours meet, the lines match')}</span>
                <span className='cvat-align-review-spacer' />
                <span className='cvat-align-review-zoom'>{`${Math.round(zoom * 100)}%`}</span>
                <Popover
                    trigger='click'
                    placement='topRight'
                    content={<Controls controls={adjust ? ADJUST_CONTROLS : VIEW_CONTROLS} />}
                >
                    <Button type='text' size='small' icon={<QuestionCircleOutlined />}>{t('Controls')}</Button>
                </Popover>
            </div>
        </>
    );
}
